import type { Translations } from "./it";

export const en: Translations = {
  // Navigation
  nav: {
    home: "Home",
    gallery: "Gallery",
    landscapes: "Landscapes",
    portraits: "Portraits",
    abstract: "Abstract",
    about: "About",
    contact: "Contact",
    shop: "Shop",
    shopAll: "Shop all",
    print: "Print",
    original: "Original",
    cart: "Cart",
    admin: "Admin",
    myOrders: "My Orders",
    login: "Sign In",
    logout: "Sign Out",
    account: "Account",
  },

  // Home page
  home: {
    heroTitle: "Art that tells stories",
    heroSubtitle: "Art for lived-in spaces.",
    heroCta: "Shop all",
    categoriesTitle: "Shop",
    categoriesSubtitle: "Explore by category",
    featuredTitle: "Featured Works",
    featuredSubtitle: "Selected for you",
    viewAll: "View All",
    aboutTitle: "The Artist",
    aboutText: "Every brushstroke tells a story. Discover the world of Dalila Di Maria through her original works.",
    aboutCta: "Learn More",
    mailingListTitle: "Join my mailing list",
    mailingListText: "Be the first to hear about new collections",
    mailingListSubmit: "Join the mailing list",
    mailingListSuccess: "Thank you — you are on the mailing list.",
  },

  // Categories
  categories: {
    print: "Print",
    printDesc: "A selection of Fine Art prints from my original paintings, available in different sizes.",
    original: "Original",
    originalDesc: "One-of-a-kind hand-painted works",
    landscapes: "Landscapes",
    landscapesDesc: "The beauty of nature captured on canvas",
    portraits: "Portraits",
    portraitsDesc: "The soul of people through colour",
    abstract: "Abstract",
    abstractDesc: "Emotions and forms beyond reality",
  },

  // Product/Artwork
  artwork: {
    price: "Price",
    technique: "Technique",
    dimensions: "Dimensions",
    year: "Year",
    category: "Category",
    available: "Available",
    sold: "Sold",
    reserved: "Reserved",
    reservationNotice: "This artwork is currently reserved for another potential buyer while they complete payment. It will be released automatically within 30 minutes if the purchase is not completed.",
    addToCart: "Purchase",
    buyNow: "Buy Now",
    inquire: "Inquire",
    relatedWorks: "Related Works",
    details: "Details",
    shipping: "Shipping",
    printInfo: "High-quality fine art prints made on premium 100% cotton paper to keep every detail and color of my original paintings beautiful for a lifetime.",
    shippingInfo: "Shipping costs are included in the price.",
    printShippingInfo: "For prints, shipping within Italy is included in the price. International orders have an additional shipping charge, calculated at checkout.",
    trackingInfo: "An international tracking link will be provided once the piece is shipped.",
  },

  // Cart & Checkout
  cart: {
    title: "Your Cart",
    empty: "Your cart is empty",
    continueShopping: "Continue Exploring",
    subtotal: "Subtotal",
    shipping: "Shipping",
    total: "Total",
    checkout: "Proceed to Payment",
    remove: "Remove",
  },

  // Checkout
  checkout: {
    title: "Payment",
    customerInfo: "Contact Information",
    name: "Full Name",
    email: "Email",
    confirmEmail: "Confirm email",
    guestEmailHelp: "Confirm your email to receive your order confirmation.",
    emailsDoNotMatch: "Email addresses do not match.",
    phone: "Phone",
    shippingAddress: "Shipping Address",
    address: "Address",
    city: "City",
    postalCode: "Postal Code",
    postcode: "Postcode",
    zipCode: "ZIP code",
    state: "State",
    province: "Province",
    stateTerritory: "State / Territory",
    country: "Country",
    notes: "Notes (optional)",
    pay: "Pay Now",
    processing: "Processing...",
    secure: "Secure payment with Stripe",
    shippingIncluded: "Included",
    shippingCalculated: "Calculated after selecting a country",
    shippingCalculating: "Calculating…",
  },

  // Contact
  contact: {
    title: "Get in Touch",
    subtitle: "Have questions about a piece or want to commission a custom work?",
    name: "Name",
    email: "Email",
    subject: "Subject",
    message: "Message",
    tiktok: "Talk to me on TikTok",
    send: "Send Message",
    success: "Message sent successfully! I'll get back to you soon.",
    error: "Error sending message. Please try again.",
  },

  // About
  about: {
    title: "About Me",
    subtitle: "My name is Dalila Di Maria.",
    intro: "I paint quiet worlds shaped by nature, memory, and the stories that stay with us.",
    invitation: "My work is an invitation to pause and notice the small beauty of everyday life.",
    quote: "Painting is stronger than I am; it always makes me do what it wants.",
    quoteAuthor: "Pablo Picasso",
    artworkAlt: "Floral painting with birds",
    revealColor: "Show painting in colour",
    studioTitle: "My Studio",
    processTitle: "My Creative Process",
    commissionsTitle: "Commissions",
    commissionsText: "I accept commissions for custom works. Contact me to discuss your idea.",
    commissionsCta: "Request a Commission",
  },

  // Auth
  auth: {
    login: "Sign In",
    register: "Sign Up",
    email: "Email",
    password: "Password",
    confirmPassword: "Confirm Password",
    forgotPassword: "Forgot Password?",
    loginWithGoogle: "Sign in with Google",
    loginWithEmail: "Password sign-in",
    noAccount: "Don't have an account?",
    hasAccount: "Already have an account?",
    createAccount: "Create Account",
    createPassword: "Create a password",
    collectorSignIn: "Collector sign in",
    createCollectorAccount: "Create collector account",
    accountDescription: "Use your account to save checkout details and manage orders from the Dalila studio.",
    magicLinkTitle: "Sign in with your email",
    magicLinkDescription: "We will send you a secure sign-in link. If this is your first visit, your account will be created automatically.",
    sendMagicLink: "Send sign-in link",
    magicLinkSent: "Check your email for your sign-in link.",
    checkInbox: "If that address can receive email, a sign-in link is on its way. The link expires after 15 minutes.",
    passwordLoginDescription: "Sign in to your account with your email and password.",
    passwordRegisterDescription: "Create an account with a secure password to manage your orders.",
    passwordHint: "Use at least 5 characters.",
    continueWithGoogle: "Continue with Google",
    orContinueWith: "or continue with",
    googleNotConfigured: "Google sign-in is not available yet.",
    googleCancelled: "Google sign-in was cancelled.",
    googleFailed: "Google sign-in could not be completed. Please try again.",
    passwordsDoNotMatch: "Passwords do not match.",
    legacyPasswordHelp: "Do you have an existing account from the previous email sign-in? Enter your email above and use the link once to set a password in your account.",
    legacyPasswordAction: "Send a temporary sign-in link",
    legacyPasswordEmailRequired: "Enter your email address first.",
  },

  // Account
  account: {
    title: "My Account",
    subtitle: "Manage your profile and view your orders from the Dalila studio.",
    welcome: "Welcome",
    roleUser: "Collector",
    roleAdmin: "Administrator",
    profile: "Profile",
    name: "Name",
    email: "Email",
    orders: "My Orders",
    ordersEmpty: "You don't have any orders yet.",
    orderNumber: "Order no.",
    adminPanel: "Admin Panel",
    logout: "Sign Out",
    passwordTitle: "Password",
    passwordDescription: "Set a password to sign in without using an email link.",
    setPassword: "Save password",
    passwordSaved: "Password saved.",
  },

  orderStatus: {
    pending: "Pending",
    paid: "Paid",
    processing: "Processing",
    shipped: "Shipped",
    delivered: "Delivered",
    cancelled: "Cancelled",
    refunded: "Refunded",
  },

  // Footer
  footer: {
    rights: "All rights reserved",
    privacy: "Privacy",
    terms: "Terms & Conditions",
    followMe: "Follow Me",
    newsletter: "Newsletter",
    newsletterText: "Subscribe to receive updates on new works",
    subscribe: "Subscribe",
    emailPlaceholder: "Your email",
    shippingPolicy: "Shipping policy",
    returnPolicy: "Return policy",
  },

  // Common
  common: {
    loading: "Loading...",
    error: "An error occurred",
    retry: "Retry",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    close: "Close",
    search: "Search",
    filter: "Filter",
    sortBy: "Sort by",
    noResults: "No results",
    viewMore: "View More",
    backToHome: "Back to Home",
  },

  // Order confirmation
  orderConfirmation: {
    title: "Order Confirmed!",
    subtitle: "Thank you for your purchase",
    orderNumber: "Order Number",
    emailSent: "We've sent you a confirmation email.",
    continueShopping: "Continue Exploring",
    processingTitle: "Payment received",
    processingSubtitle: "We are securely verifying your payment. This page will update automatically.",
    processingEmail: "The confirmation email will only be sent after the payment is validated.",
    processingError: "We could not check the status yet. If the payment is confirmed, you will receive the order email.",
    cancelledTitle: "Payment cancelled",
    cancelledSubtitle: "No charge was made and no order was created. The artwork will become available again when the temporary reservation expires.",
    backToGallery: "Back to gallery",
  },
} as const;
